import { FC } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  DefaultZIndexes,
  LabelList,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type BarRectangleItem,
} from 'recharts';
import type { ChartBindingValue, CustomChartPoint } from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import { chartElementColorClass, type ChartAppearance } from '../chartElements';
import { ChartPointTooltip } from './ChartPointTooltip';
import { CHART_CATEGORY_MAX_WIDTH, CHART_SERIES_COLOR, CHART_TRACK_COLOR } from './chartAxis';
import { ChartCategoryAxis } from './ChartCategoryAxis';
import { ChartCategoryTick } from './ChartCategoryTick';
import { ChartValueLabel, VALUE_LABEL_GAP, VALUE_LABEL_ROW_HEIGHT } from './ChartValueLabel';

/** Высота строки одной полосы: сама полоса и зазор до соседней. */
const BAR_ROW_HEIGHT = 28;

/** Толщина полосы и столбца — та же, что у полос показателей стандартного дашборда. */
const BAR_THICKNESS = 16;

/** Скругление концов полосы: половина толщины даёт полностью круглый конец. */
const BAR_RADIUS = BAR_THICKNESS / 2;

/**
 * Наименьшая видимая длина заполненной части: у величины около нуля полоса иначе пропала бы.
 *
 * Короче толщины её брать нельзя: скругление заполнения обрезается по его собственной длине, и
 * короткое заполнение занимает полную толщину там, где дорожка уже сужена своим скруглением, —
 * то есть торчит за её краем. Длиной в толщину заполнение остаётся кругом внутри конца дорожки.
 */
const BAR_MIN_LENGTH = BAR_THICKNESS;

/**
 * Длина заполненной части у самой малой величины — и её отсутствие у нуля.
 *
 * Нулю наименьшая длина не положена: заполнять дорожку ему нечем, а отличить его от малой
 * ненулевой величины иначе было бы нельзя. Сам ноль виден подписью величины и незаполненной
 * дорожкой — их элементу оставляет своя фигура заполнения (см. `BAR_FILL_SHAPE`).
 */
const barMinLength = (value: number | null | undefined): number => (value ? BAR_MIN_LENGTH : 0);

/**
 * Заполненная часть — прямоугольник самой библиотеки, но отданный ей своей фигурой элемента.
 *
 * Рисунок от этого не меняется: фигура та же, что библиотека взяла бы сама, и у нулевой длины она
 * не рисует ничего. Отдана она затем, что элемент нулевой длины библиотека иначе выбрасывает
 * целиком — вместе с дорожкой и подписью величины, оставляя от элемента одно название, — и
 * сохраняет его только там, где отрисовку задали снаружи.
 */
const BAR_FILL_SHAPE = <Rectangle />;

/**
 * Дорожка нарисована над подсветкой наведения, но под заполненной частью: своим слоем библиотека
 * кладёт дорожку ниже подсветки, и та закрыла бы собой и дорожку, и незаполненный остаток.
 */
const TRACK_Z_INDEX = DefaultZIndexes.bar - 1;

/** Скругление подсветки наведения — то же, что у строки показателя стандартного дашборда. */
const HOVER_HIGHLIGHT_RADIUS = 6;

/**
 * Подсветка наведения: приглушённый фон полосы категории — как у строки показателя стандартного
 * дашборда. Обводка снята: её цвет библиотека задаёт значением, и в тёмной теме он остался бы
 * светлым.
 */
const HOVER_HIGHLIGHT = {
  fill: CHART_TRACK_COLOR,
  fillOpacity: 0.5,
  stroke: 'none',
  radius: HOVER_HIGHLIGHT_RADIUS,
};

/** Высота места под названия под столбцами и высота самого рисунка вертикальной диаграммы. */
const CATEGORY_AXIS_HEIGHT = 24;
const VERTICAL_CHART_HEIGHT = 220;

/**
 * Место над столбцами отведено под две строки подписей величин всегда.
 *
 * Разводятся подписи на две строки или умещаются в одну, видно только по ширине рисунка, а её знает
 * сама библиотека — уже после того, как поле под подписи ей задано. Мерить рисунок своим
 * наблюдателем ради этого не стоит: перерисовка каждой диаграммы дашборда на каждый пиксель
 * изменения ширины окна дороже пустой строки, а высоты рисунка она стоит один раз.
 */
const VALUE_LABEL_ROWS = 2;

/**
 * Доля заполнения дорожки в долях единицы. Шкала задана явно: подобранная библиотекой по данным,
 * она округлялась бы вверх до «круглой» отметки, и наибольший элемент не доходил бы до конца.
 */
const SHARE_DOMAIN: [number, number] = [0, 1];

/** Точка вместе с тем, чем её рисуют: долей заполнения и показанной величиной. */
export type BarChartDatum = CustomChartPoint & {
  /** Доля заполнения дорожки: от собственного потолка либо от наибольшей величины диаграммы */
  plotted: number | null;
  valueText: string;
};

type Props = {
  /** Элементы одной группы выборки в порядке показа */
  items: BarChartDatum[];
  /** Направление элементов: столбцы вверх либо полосы вправо */
  isVertical: boolean;
  /** Ширина самой длинной величины диаграммы: место под неё общее у групп, и дорожки их рядов
   * кончаются на одной черте */
  valueTextWidth: number;
  /** Пороги зон качества пользователя: по ним окрашивается элемент со своим потолком */
  zoneRanges: QualityZoneRanges;
  /** Оформление диаграммы: цвета категорий и правила раскраски */
  appearance?: ChartAppearance;
  /** Выбор элемента наполняет связанную диаграмму; не задан — элементы не выбираются */
  onSelectElement?: (value: ChartBindingValue) => void;
  /** Выбранный элемент — он подсвечен, повторный выбор снимает его */
  selectedElement?: ChartBindingValue;
};

/**
 * Ряд элементов одной группы столбчатой диаграммы: дорожки, их заполненные части, названия и
 * величины.
 *
 * Что показывать, решает сама диаграмма — ряд получает уже готовые к рисованию элементы и общую
 * на все группы мерку места под величину: без неё дорожки соседних групп кончались бы каждая на
 * своей черте.
 *
 * Высота полос растёт по числу категорий, а не подгоняется под карточку: у полутора десятков
 * категорий подогнанные полосы слились бы в заливку. Не поместившееся прокручивается карточкой.
 */
export const BarChartGroup: FC<Props> = ({
  items,
  isVertical,
  valueTextWidth,
  zoneRanges,
  appearance,
  onSelectElement,
  selectedElement,
}) => {
  const valueLabelWidth = valueTextWidth + VALUE_LABEL_GAP;

  /**
   * Выбор элемента поставлен на всей диаграмме, а не на одной его заполненной части: заполнять
   * дорожку нулю нечем, и щелчок по нулевому элементу не доходил бы никуда. Дорожка же есть у
   * каждого элемента, и щелчок по ней библиотека отдаёт сюда вместе с самой точкой.
   */
  const handleElementClick = onSelectElement
    ? (element: BarRectangleItem) => {
        const point: BarChartDatum | undefined = element.payload;
        if (point) onSelectElement(point.label);
      }
    : undefined;

  return (
    <ResponsiveContainer width="100%" height={isVertical ? VERTICAL_CHART_HEIGHT : items.length * BAR_ROW_HEIGHT}>
      <BarChart
        data={items}
        layout={isVertical ? 'horizontal' : 'vertical'}
        // Поле отведено под подпись величины: она рисуется за концом дорожки, а дорожка занимает
        // всю область рисования — без поля величина ушла бы за край рисунка.
        margin={
          isVertical
            ? { top: VALUE_LABEL_ROW_HEIGHT * VALUE_LABEL_ROWS, right: 0, bottom: 0, left: 0 }
            : { top: 0, right: valueLabelWidth, bottom: 0, left: 0 }
        }
      >
        {/*
          Ось величины скрыта, но остаётся шкалой: по ней полоса и отмеряет своё заполнение.
          Показывать её нечего — величина подписана у самого элемента.
        */}
        {isVertical ? (
          <XAxis
            type="category"
            dataKey="label"
            height={CATEGORY_AXIS_HEIGHT}
            interval={0}
            tickSize={0}
            tickMargin={0}
            tick={<ChartCategoryTick width={CHART_CATEGORY_MAX_WIDTH} placement="bottom" />}
            tickLine={false}
            axisLine={false}
          />
        ) : (
          <XAxis type="number" domain={SHARE_DOMAIN} hide />
        )}
        {isVertical ? (
          <YAxis type="number" domain={SHARE_DOMAIN} hide />
        ) : (
          <ChartCategoryAxis dataKey="label" valueWidth={valueLabelWidth} />
        )}
        <Tooltip content={<ChartPointTooltip />} cursor={HOVER_HIGHLIGHT} />
        <Bar
          dataKey="plotted"
          fill={CHART_SERIES_COLOR}
          // Дорожка во всю отведённую длину: по ней видно не только «сколько», но и «сколько из
          // возможного» — остаток до потолка, который и красится зоной.
          background={{ fill: CHART_TRACK_COLOR, radius: BAR_RADIUS, zIndex: TRACK_Z_INDEX }}
          radius={BAR_RADIUS}
          shape={BAR_FILL_SHAPE}
          // По горизонтали полоса всегда своей толщины — высота диаграммы растёт по числу строк.
          // По вертикали столбцы делят ширину между собой: у частого ряда толщина ограничена
          // сверху, иначе соседние столбцы наложились бы друг на друга.
          barSize={isVertical ? undefined : BAR_THICKNESS}
          maxBarSize={BAR_THICKNESS}
          minPointSize={barMinLength}
          isAnimationActive={false}
          className={onSelectElement ? 'cursor-pointer' : undefined}
          onClick={handleElementClick}
        >
          {items.map((item) => (
            <Cell
              key={item.label}
              // Цвет — класс заливки: он задан утилитой проекта, и она доходит до узла библиотеки,
              // а презентационный `fill` классу уступает.
              className={chartElementColorClass(item, appearance, zoneRanges)}
              fillOpacity={selectedElement !== undefined && selectedElement !== item.label ? 0.4 : 1}
            />
          ))}
          <LabelList
            dataKey="valueText"
            offset={VALUE_LABEL_GAP}
            content={
              <ChartValueLabel
                placement={isVertical ? 'top' : 'end'}
                count={items.length}
                valueTextWidth={valueTextWidth}
              />
            }
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};
